import type { DropdownMenuRadioGroup } from "@radix-ui/react-dropdown-menu";
import type { RegisteredRouter, useLocation } from "@tanstack/react-router";
import type { i18n } from "i18next";
import type { ComponentProps } from "react";

import type { LanguageCode, useTranslation2 } from "~/shared/i18n";
import { EMPTY, HASH, SLASH } from "~/shared/str";
import type { ToggleGroup } from "~/shared/ui/toggle-group";

type SingleToggleGroupProps = Extract<
  ComponentProps<typeof ToggleGroup>,
  {
    type: "single";
  }
>;

function generateValueProps(
  i18n: i18n,
  router: RegisteredRouter,
  location: ReturnType<typeof useLocation<RegisteredRouter>>,
): Pick<
  SingleToggleGroupProps | ComponentProps<typeof DropdownMenuRadioGroup>,
  "value" | "onValueChange"
> {
  return {
    value: i18n.language,
    onValueChange: (value) => {
      if (value.length === 0 || value === i18n.language) return;

      void i18n.changeLanguage(value);

      const { hash, pathname, searchStr, state } = location;
      router.history.replace(
        `${SLASH}${[value, ...pathname.split(SLASH).slice(2)].join(SLASH)}${searchStr}${
          hash.length > 0 ? [HASH, hash].join(EMPTY) : EMPTY
        }`,
        state,
      );
    },
  };
}

function translateLanguageCode(
  t: ReturnType<typeof useTranslation2>["translation"]["t"],
  code: LanguageCode,
) {
  return t(code.toLocaleUpperCase(), {
    lng: code,
  }).toLocaleUpperCase();
}

export { generateValueProps, translateLanguageCode };
export type { SingleToggleGroupProps };
